import { useState } from 'react';
import { Button, useMediaQuery, Checkbox, styled } from '@mui/material';
import config from '../config.json';
import {
  deaNumUsersAllSelected,
  IDeaNumUser,
  IUser,
  searchEmailsMinLength,
  StyledAutocomplete2,
  StyledTablePagination,
  SuccessIcon,
  toggleItem,
  ValueLabel,
} from '../common';
import {
  IRequestApiCallStatus,
  StatusSaveRequst,
  TeamRequestAction,
} from '../team-request';
// import { Justification, justificationOptions } from '../request';
import {
  excludeUserRoles,
  useEmailSearch,
  useSerialNumberSearch,
  useUserRoleSearch,
} from '../admin';
import { UserRoles2d, UserRolesStack } from '.';
import { always, pathOr } from 'rambda';
import { left, right, map as emap } from 'fp-ts/lib/Either';
import { map } from 'fp-ts/Array';
import { flow, pipe } from 'fp-ts/lib/function';
import {
  noop,
  compose as lcompose,
  tap as ltap,
  trim as ltrim,
} from 'lodash/fp';

const properties: ValueLabel<keyof IDeaNumUser>[] = [
  { value: 'deaNumber', label: 'DEA Registration Number' },
  { value: 'deaExpDt', label: 'DEA Registration Expiration' },
  { value: 'role', label: 'Role' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'loginEmail', label: 'Email' },
  { value: 'icamUuid', label: 'Account Id' },
  { value: 'phone', label: 'Phone' },
  { value: 'address', label: 'Business Mailing Address' },
  // { value: 'status', label: 'Certificate State' },
  { value: 'idExpirationDt', label: 'Identity Proofing Expiration' },
  { value: 'numberOfRenewal', label: 'Number of Renewal' },
  { value: 'serialNumber', label: 'Certificate Serial Number' },
  { value: 'certStatus', label: 'Certificate Status' },
  { value: 'certExpDt', label: 'Certificate Expiration' },
  { value: 'updatedAt', label: 'Certificate Change Date' },
];

const rowsPerPageOptions = [10, 25, 50, 100, 500];

export const Request = ({
  forRegistrantsNCoordinators,
  user,
  searchUsers,
  raTab,
  diTab,
}: {
  forRegistrantsNCoordinators: boolean;
  user?: IUser;
  searchUsers: boolean;
  raTab?: boolean;
  diTab?: boolean;
}) => {
  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  const { emails, emailInput, setEmailInput } = useEmailSearch({
    forRegistrantsNCoordinators,
  });
  const { serialNumbers, serialNumberInput, setSerialNumberInput } =
    useSerialNumberSearch({ forRegistrantsNCoordinators });

  const activeEmailOptions = emails.map(e => e.email);
  const activeSerialNumberOptions = serialNumbers.map(s => s.serialNumber);


  const [email, setEmail] = useState<string | null>(null);
  const [serialNumber, setSerialNumber] = useState<string | null>(null);
  const [deaNumber, setDeaNumber] = useState<string | null>(null);

  const { userRoles } = useUserRoleSearch({
    forRegistrantsNCoordinators,
    email,
    serialNumber,
    deaNumber,
  });

  const filteredUserRoles = (raTab || diTab)
    ? userRoles.filter(u => u.active)
    : userRoles;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = flow(
    pathOr('10', 'target.value'),
    (a: string) => parseInt(a, 10),
    setPageSize,
    always(0),
    setPage
  );

  const [selectedUserRoleKeys, setSelectedUserRoleKeys] = useState<string[]>(
    []
  );
  const toggleUserRole = ({ type, payload }: TeamRequestAction) =>
    type === 'ToggleUserRole'
      ? payload === 'all'
        ? toggleUserRoleAll()
        : pipe(
          payload,
          toggleItem(selectedUserRoleKeys),
          setSelectedUserRoleKeys
        )
      : void 0;

  const toggleUserRoleAll = () =>
    pipe(
      deaNumUsersAllSelected({
        deaNumUsers: excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles),
        selectedUserRoleKeys,
      }),
      allSelected =>
        allSelected
          ? setSelectedUserRoleKeys([])
          : setSelectedUserRoleKeys(
            pipe(
              excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles),
              map<IDeaNumUser, string>(pathOr('', 'key'))
            )
          )
    );

  // const [justification, setJustification] = useState('');
  const [otherComments, setOtherComments] = useState('');

  const [apiCallStatus, setApiCallStatus] = useState<IRequestApiCallStatus>({
    type: 'ConfirmSubmitRequest',
    apiCallStatus: void 0,
  });

  const [revokedUserRoleKeys] = useState<string[]>([]);

  return (
    <Wrapper>
      <article>
        <h3>Search User Certificates</h3>
        <FilterWrapper>
          <ActionLabel id="email">Email:</ActionLabel>
          <StyledAutocomplete2
            value={email}
            labelledby="email"
            inputValue={emailInput}
            onInputChange={lcompose(
              ltap(
                lcompose(emap(lcompose(setEmail, always(null))), (a: string) =>
                  a === '' ? right(void 0) : left(void 0)
                )
              ),
              ltap(setEmailInput),
              ltrim
            )}
            options={activeEmailOptions}
            onChange={flow(
              setEmail,
              () => setDeaNumber(null),
              () => setSerialNumber(null)
            )}
            noOptionsText={
              emailInput.length < searchEmailsMinLength
                ? `Please enter minimum ${searchEmailsMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />

          <FilterLabel id="deaNum">DEA Registration Number:</FilterLabel>
          <input
            aria-labelledby="deaNum"
            value={deaNumber || ''}
            onChange={flow(
              pathOr('', 'target.value'), // always returns a string
              ltrim, // trims leading/trailing whitespace
              setDeaNumber,
              () => setEmail(null),
              () => setSerialNumber(null)
            )}
            type="text"
          />

          <FilterLabel id="serialNumber">
            Certificate Serial Number:
          </FilterLabel>

          <StyledAutocomplete2
            value={serialNumber}
            labelledby="serialNumber"
            inputValue={serialNumberInput}
            onInputChange={lcompose(
              ltap(
                lcompose(
                  emap(lcompose(setSerialNumber, always(null))),
                  (a: string) => (a === '' ? right(void 0) : left(void 0))
                )
              ),
              ltap(setSerialNumberInput),
              ltrim
            )}
            options={activeSerialNumberOptions}
            onChange={flow(
              setSerialNumber,
              () => setEmail(null),
              () => setDeaNumber(null)
            )}
            noOptionsText={
              serialNumberInput.length < searchEmailsMinLength
                ? `Please enter minimum ${searchEmailsMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />
        </FilterWrapper>
      </article>
      <SectionWrapper>
        <h3>
          {searchUsers ? 'User List' : ''}{' '}
          {!matchesGridBreakpoint && (
            <Checkbox
              color="primary"
              onClick={toggleUserRoleAll}
              checked={deaNumUsersAllSelected({
                deaNumUsers: excludeUserRoles(revokedUserRoleKeys)(filteredUserRoles),
                selectedUserRoleKeys,
              })}
            />
          )}
        </h3>

        {matchesGridBreakpoint ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <StyledTablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                count={filteredUserRoles.length}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
            <UserRoles2d
              deaNumUsers={filteredUserRoles.slice(
                page * pageSize,
                (page + 1) * pageSize
              )}
              selectedUserRoleKeys={selectedUserRoleKeys}
              enableAll
              dispatchTeamRequest={toggleUserRole}
            />
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <StyledTablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                count={filteredUserRoles.length}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <StyledTablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                count={filteredUserRoles.length}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
            <UserRolesStack
              deaNumUsers={filteredUserRoles.slice(
                page * pageSize,
                (page + 1) * pageSize
              )}
              selectedUserRoleKeys={selectedUserRoleKeys}
              dispatchTeamRequest={toggleUserRole}
              properties={properties}
            />
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <StyledTablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                count={filteredUserRoles.length}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </>
        )}
      </SectionWrapper>

      {searchUsers ? null : (
        <>
          <Actions>
            {/* <Justification
              value={justification}
              options={justificationOptions.REVOKE}
              onChange={setJustification}
              otherComments={otherComments}
              onChangeOtherComments={setOtherComments}
              tooltipValue={always(false)}
              toggleTooltip={noop}
              offTooltip={noop}
            /> */}
          </Actions>

          <StatusSaveRequst apiCallStatus={apiCallStatus.apiCallStatus} />
          {apiCallStatus.apiCallStatus === true && (
            <article>
              <SuccessIcon />
              <span> {config.requestSubmitSuccess} </span>
            </article>
          )}
          <Button
            onClick={() =>
              setApiCallStatus(a => ({ ...a, apiCallStatus: null }))
            }
            disabled
            variant="contained"
            color="primary">
            submit
          </Button>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '3rem',
  },
}));

const FilterWrapper = styled('span')(({ theme }) => ({
  margin: '1.5rem',
  display: 'grid',
  gap: '0.5rem',

  '@media (min-width: 33.375em)': {
    display: 'grid',
    gridTemplateColumns: '200px  300px 200px 300px ',
    gap: '0.5rem',
    alignItems: 'center',
  },
}));

const SectionWrapper = styled('article')(({ theme }) => ({
  width: 'fit-content',

  '&>*+*': {
    marginTop: '1rem',
  },
}));

const ActionLabel = styled('span')(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
  },
}));

const FilterLabel = styled('span')(({ theme }) => ({
  marginTop: '0.5rem',

  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
    marginLeft: '5rem',
    marginTop: '0rem',
  },
}));

const Actions = styled('article')(({ theme }) => ({
  display: 'grid',
  gap: '0.5rem',
  alignItems: 'center',
  gridAutoRows: 'minmax(1rem,auto)',

  '@media (min-width: 33.375em)': {
    gridTemplateColumns: 'auto 200px auto auto auto 1fr',
  },
}));
